<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import {
  ChevronLeft,
  Users,
  ClipboardCheck,
  BarChart3,
  CalendarDays,
  FileText,
  StickyNote,
  Clock,
  MoreHorizontal,
  Settings,
} from 'lucide-vue-next'
import { AppButton, EntityIcon, type PaletteHue } from '@/design-system/components'

/** Class workspace header — class.soft → white wash, identity, next-lesson chip, tabs. */
export type ClassTab = 'students' | 'attendance' | 'grades' | 'schedule' | 'reports' | 'notes'

const props = defineProps<{
  hue: PaletteHue
  name: string
  studentCount: number
  subject?: string | null
  metaLine?: string | null
  nextLabel?: string | null
  activeTab: ClassTab
}>()
const emit = defineEmits<{
  select: [tab: ClassTab]
  takeRegister: []
  back: []
  settings: []
}>()

const menuOpen = ref(false)
const menuRef = ref<HTMLElement | null>(null)

function onClickOutside(e: MouseEvent) {
  if (menuRef.value && !menuRef.value.contains(e.target as Node)) menuOpen.value = false
}
onMounted(() => document.addEventListener('click', onClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside))

const TABS: { key: ClassTab; label: string; icon: typeof Users }[] = [
  { key: 'students', label: 'Students', icon: Users },
  { key: 'attendance', label: 'Attendance', icon: ClipboardCheck },
  { key: 'grades', label: 'Grades', icon: BarChart3 },
  { key: 'schedule', label: 'Schedule', icon: CalendarDays },
  { key: 'reports', label: 'Reports', icon: FileText },
  { key: 'notes', label: 'Notes', icon: StickyNote },
]

void props
</script>

<template>
  <div class="cwh">
    <div
      class="cwh__wash"
      :style="{ background: `linear-gradient(180deg, ${hue.soft} 0%, #ffffff 100%)` }"
    />
    <div class="cwh__inner">
      <button type="button" class="cwh__back" :style="{ color: hue.ink }" @click="emit('back')">
        <ChevronLeft :size="16" /> All classes
      </button>

      <div class="cwh__identity">
        <EntityIcon :icon="Users" :palette="hue" :size="52" :corner-radius="15" />
        <div class="cwh__id">
          <div class="cwh__titlerow">
            <h1 class="cwh__name">{{ name }}</h1>
            <span v-if="subject" class="cwh__subject" :style="{ background: hue.soft, color: hue.ink }">
              {{ subject }}
            </span>
          </div>
          <div class="cwh__meta">{{ metaLine || `${studentCount} students` }}</div>
        </div>

        <div v-if="nextLabel" class="cwh__chip">
          <span class="cwh__chip-tile" :style="{ background: hue.soft, color: hue.ink }">
            <Clock :size="13" />
          </span>
          <span class="cwh__chip-text">
            <span class="cwh__chip-eyebrow">NEXT LESSON</span>
            <span class="cwh__chip-time">{{ nextLabel }}</span>
          </span>
        </div>

        <div class="cwh__actions">
          <AppButton variant="primary" size="sm" @click="emit('takeRegister')">
            <ClipboardCheck :size="15" /> Take register
          </AppButton>
          <div ref="menuRef" class="cwh__menuwrap">
            <button
              type="button"
              class="cwh__kebab"
              aria-label="More"
              :aria-expanded="menuOpen"
              @click="menuOpen = !menuOpen"
            >
              <MoreHorizontal :size="18" />
            </button>
            <div v-if="menuOpen" class="cwh__menu">
              <button type="button" class="cwh__menuitem" @click="menuOpen = false; emit('settings')">
                <Settings :size="15" /> Class settings
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="cwh__tabs">
        <button
          v-for="t in TABS"
          :key="t.key"
          type="button"
          class="cwh__tab"
          :class="{ 'cwh__tab--active': activeTab === t.key }"
          @click="emit('select', t.key)"
        >
          <component :is="t.icon" :size="17" :color="activeTab === t.key ? hue.ink : 'var(--ink-400)'" />
          {{ t.label }}
          <span v-if="activeTab === t.key" class="cwh__underline" :style="{ background: hue.solid }" />
        </button>
      </div>
    </div>
    <div class="cwh__divider" />
  </div>
</template>

<style scoped>
.cwh {
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
}
.cwh__wash {
  position: absolute;
  inset: 0;
}
.cwh__inner {
  position: relative;
  padding: 16px 26px 0;
}
.cwh__back {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0;
  margin-bottom: 14px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
}
.cwh__identity {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.cwh__id {
  flex: 1;
  min-width: 0;
}
.cwh__titlerow {
  display: flex;
  align-items: center;
  gap: 10px;
}
.cwh__name {
  margin: 0;
  font-size: 26px;
  font-weight: 600;
  letter-spacing: -0.5px;
  color: var(--text-primary);
}
.cwh__subject {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: var(--radius-sm);
}
.cwh__meta {
  margin-top: 4px;
  font-size: 13.5px;
  color: var(--ink-600);
}
.cwh__chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 12px 7px 8px;
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-md);
  background: var(--surface);
}
.cwh__chip-tile {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.cwh__chip-text {
  display: flex;
  flex-direction: column;
}
.cwh__chip-eyebrow {
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 1px;
  color: var(--ink-400);
}
.cwh__chip-time {
  font-family: var(--font-mono);
  font-variant-numeric: tabular-nums;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}
.cwh__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cwh__kebab {
  width: 36px;
  height: 36px;
  border: 1px solid var(--outline-variant);
  border-radius: 9px;
  background: var(--surface);
  color: var(--ink-600);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.cwh__menuwrap {
  position: relative;
}
.cwh__menu {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  min-width: 176px;
  padding: 4px;
  background: var(--surface);
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-raise);
  z-index: 20;
}
.cwh__menuitem {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-700);
  text-align: left;
}
.cwh__menuitem:hover {
  background: var(--surface-muted);
}
.cwh__tabs {
  display: flex;
  gap: 4px;
  margin-top: 18px;
}
.cwh__tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 12px 14px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13.5px;
  font-weight: 500;
  letter-spacing: -0.1px;
  color: var(--ink-500);
}
.cwh__tab--active {
  color: var(--text-primary);
  font-weight: 600;
}
.cwh__underline {
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 0;
  height: 2.5px;
  border-radius: 3px;
}
.cwh__divider {
  position: relative;
  height: 1px;
  background: var(--outline-variant);
}
</style>
